import { Grid } from '@material-ui/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { validateVolume } from 'src/common/helpers/volume-uom-validate';
import { validateWeight } from 'src/common/helpers/weight-uom-validate';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { DEBOUNCE_TIMEOUT } from '../common/helpers/config';
import { IButton } from '../common/interfaces/IButton';
import { IKeypadState } from '../common/interfaces/states/IKeypadState';
import {
  getSalesKeypadState,
  resetSalesKeypadState,
} from '../sales-keypad/business-logic/sales-keypad-operations';
import { searchProducts } from './business-logic/search-products';
import ProductList from './components/product-list/product-list';
import SearchProductsBottomControl from './components/search-products-bottom-control/search-products-bottom-control';
import SearchProductsHeaderControl from './components/search-products-header-control/search-products-header-control';
import { styles } from './search-products-styles';

const defaultCurrentPage = 0;
const defaultLastPage = 0;
const defaultFetchSize = 20;
const defaultFetchPage = 0;

interface ISearchProductsProps {
  searchText: string;
  pageSize: number;
  submitButton: IButton;
  setTotalCount: Function;
  setIsLoading: Function;
}

const SearchProducts: React.FC<ISearchProductsProps> = ({
  searchText,
  pageSize,
  submitButton,
  setTotalCount,
  setIsLoading,
}: ISearchProductsProps): JSX.Element => {
  const initialSalesKeypadState = useRef<IKeypadState>(getSalesKeypadState());
  const [productListings, setProductListings] = useState<IItemLookup[]>([]);
  const [selectedProductListing, setSelectedProductListing] = useState<IItemLookup>();
  const [quantity, setQuantity] = useState<number>(0);
  /*Determines the page number in the front end.*/
  const [currentPage, setCurrentPage] = useState<number>(defaultCurrentPage);
  const [lastPage, setLastPage] = useState<number>(defaultLastPage);
  /*Determines the page to be fetched from backend */
  const [fetchPage, setFetchPage] = useState<number>(defaultFetchPage);
  /*
  This defines the number of products to be fetched.The number of products fetched.would be greater than or equal to 20 which is also a 
  multiple of page size
  */
  const fetchSize =
    defaultFetchSize % pageSize === 0
      ? defaultFetchSize
      : defaultFetchSize + (pageSize - (defaultFetchSize % pageSize));
  const classes = styles();
  const isMW = (selectedElements?: IItemLookup): boolean => {
    return (
      !!initialSalesKeypadState.current.isManualWeight &&
      validateWeight(selectedElements?.unitsOfMeasure)
    );
  };
  const onProductListingClick = (selectedElements: IItemLookup): void => {
    if (!selectedProductListing) {
      setSelectedProductListing(selectedElements);
      setQuantity(isMW(selectedElements) ? initialSalesKeypadState.current.metricValue : 1);
    } else {
      if (selectedElements && selectedProductListing.itemId === selectedElements.itemId) {
        setSelectedProductListing(undefined);
        setQuantity(0);
      } else {
        setSelectedProductListing(selectedElements);
        setQuantity(isMW(selectedElements) ? initialSalesKeypadState.current.metricValue : 1);
      }
    }
  };

  const onSubmitProductClick = useCallback((): void => {
    submitButton.onClick(selectedProductListing, quantity, isMW(selectedProductListing));
    resetSalesKeypadState();
  }, [selectedProductListing, quantity, submitButton]);

  // TODO: useCallback dependency is unknown. Needs to be updated
  // eslint-disable-next-line
  const searchProductListings = useCallback(
    AwesomeDebouncePromise(searchProducts, DEBOUNCE_TIMEOUT),
    [],
  );

  useEffect((): void => {
    setProductListings([]);
    setSelectedProductListing(undefined);
    setQuantity(0);
    setCurrentPage(0);
    setLastPage(0);
    setTotalCount(0);
    setFetchPage(0);
  }, [searchText, setTotalCount]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    searchProductListings(searchText.trim(), fetchPage, fetchSize)
      .then(response => {
        if (mounted) {
          /* cache the previously fetched products */
          setProductListings(oldListings => [...oldListings, ...response.productListings]);
          setTotalCount(response.total);
          setLastPage(Math.ceil(response.total / pageSize));
          setSelectedProductListing(undefined);
          setQuantity(0);
          setIsLoading(false);
        }
      })
      .catch(() => {
        const messageItem: IMessageModal = {
          message: 'CommunicationError',
          type: EmeraldDialogTypes.Error,
          subMessage: 'SearchUnavailable',
        };
        popupMessage(messageItem);
      });
    return () => {
      mounted = false;
    };
  }, [
    searchText,
    fetchSize,
    pageSize,
    setTotalCount,
    fetchPage,
    searchProductListings,
    setIsLoading,
  ]);

  return (
    <Grid container direction='column' justifyContent='space-between'>
      <Grid item className={classes.products}>
        {lastPage !== 0 && <SearchProductsHeaderControl />}
        <ProductList
          listings={productListings.slice(currentPage * pageSize, (currentPage + 1) * pageSize)}
          selectedListing={selectedProductListing}
          onClick={onProductListingClick}
        />
      </Grid>
      <Grid item className={classes.controls}>
        {lastPage !== 0 && (
          <SearchProductsBottomControl
            currentPage={Math.min(currentPage + 1, lastPage)}
            lastPage={lastPage}
            quantity={quantity}
            data-testid='add-item-button'
            quantityVisible={
              productListings.length > 0 &&
              !validateWeight(selectedProductListing?.unitsOfMeasure) &&
              !validateVolume(selectedProductListing?.unitsOfMeasure)
            }
            submitButton={{
              label: submitButton.label,
              onClick: onSubmitProductClick,
              disabled: !selectedProductListing || quantity < 1,
            }}
            onQuantityChange={setQuantity}
            nextPage={() => {
              setSelectedProductListing(undefined);
              setQuantity(0);
              if (pageSize * (currentPage + 2) >= (fetchPage + 1) * fetchSize) {
                /*if user reaches the penultimate page of fetched products, fetch more products*/
                setFetchPage(fetchPage + 1);
              }
              setCurrentPage(currentPage + 1);
            }}
            previousPage={() => {
              setSelectedProductListing(undefined);
              setQuantity(0);
              setCurrentPage(currentPage - 1);
            }}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default SearchProducts;
